import React from 'react';
import { BillingTransaction } from '../../services/billingService';
import { useAppSelector } from '../../store/hooks';
import { ArrowUp, ArrowDown, Plus, Minus, DollarSign, Calendar, User, FileText, Edit, Trash2 } from 'lucide-react';
import clsx from 'clsx';
import LoadingSpinner from '../UI/LoadingSpinner';
import Button from '../UI/Button';

interface TransactionTableProps {
  transactions: BillingTransaction[];
  loading?: boolean;
  showHostColumn?: boolean;
  onTransactionClick?: (transaction: BillingTransaction) => void;
  onEditExtraService?: (transaction: BillingTransaction) => void;
  onDeleteExtraService?: (transaction: BillingTransaction) => void;
  showActions?: boolean; // Whether to show edit/delete actions (admin only)
}

const TransactionTable: React.FC<TransactionTableProps> = ({
  transactions,
  loading = false,
  showHostColumn = false,
  onTransactionClick,
  onEditExtraService,
  onDeleteExtraService,
  showActions = false,
}) => {
  const { theme } = useAppSelector((state) => state.ui);
  const { user } = useAppSelector((state) => state.auth);
  const isAdmin = user?.role === 'admin';

  const getTransactionIcon = (transaction: BillingTransaction) => {
    if (transaction.is_topup) {
      return <Plus className="h-4 w-4 text-green-600 dark:text-green-400" />;
    }
    if (transaction.is_refund) {
      return <ArrowUp className="h-4 w-4 text-blue-600 dark:text-blue-400" />;
    }
    if (transaction.is_charge) {
      return <ArrowDown className="h-4 w-4 text-red-600 dark:text-red-400" />;
    }
    return <DollarSign className="h-4 w-4 text-gray-600 dark:text-gray-400" />;
  };

  const getTransactionTypeLabel = (transaction: BillingTransaction): string => {
    if (transaction.is_topup) return 'Top-Up';
    if (transaction.is_refund) {
      // Check if it's a refund for empty companions
      if (transaction.is_empty_companion || transaction.description?.toLowerCase().includes('empty companion')) {
        return 'Refund: Empty Companion';
      }
      return 'Refund';
    }
    
    switch (transaction.transaction_type) {
      case 'setup_fee':
        return 'Setup Fee';
      case 'guest_base_fee':
        return 'Guest Base Fee';
      case 'companion_fee':
        // Check if it's for empty companions
        if (transaction.is_empty_companion || transaction.description?.toLowerCase().includes('empty companion')) {
          return 'Empty Companion Fee';
        }
        return 'Companion Fee';
      case 'image_message_fee':
        return 'Image Message';
      case 'save_the_date_fee':
        return 'Save the Date';
      case 'extra_service':
        // Show service name if available
        if (transaction.service_name) {
          return `Extra Service: ${transaction.service_name}`;
        }
        return 'Extra Service';
      case 'usher_service_fee':
        return 'Usher Service Fee';
      default:
        return 'Charge';
    }
  };

  const formatAmount = (amount: string): string => {
    const num = parseFloat(amount);
    return `${num >= 0 ? '+' : ''}${num.toFixed(2)} JOD`;
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  if (transactions.length === 0) {
    return (
      <div className={`text-center py-12 rounded-lg ${
        theme === 'dark' ? 'bg-gray-900/50' : 'bg-gray-50'
      }`}>
        <FileText className={`h-12 w-12 mx-auto mb-4 ${
          theme === 'dark' ? 'text-gray-600' : 'text-gray-400'
        }`} />
        <p className={`text-lg font-medium ${
          theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
        }`}>
          No transactions found
        </p>
        <p className={`text-sm mt-2 ${
          theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
        }`}>
          Transactions will appear here once billing activity occurs
        </p>
      </div>
    );
  }

  return (
    <div className={`rounded-lg shadow overflow-hidden ${
      theme === 'dark' 
        ? 'bg-gray-900/80 border border-gray-700/50' 
        : 'bg-white border border-gray-200'
    }`}>
      <div className="overflow-x-auto">
        <table className={`w-full divide-y ${
          theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'
        }`}>
          <thead className={theme === 'dark' ? 'bg-gray-800/50' : 'bg-gray-50'}>
            <tr>
              <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
              }`}>
                Type
              </th>
              {showHostColumn && (
                <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                }`}>
                  Host
                </th>
              )}
              <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
              }`}>
                Description
              </th>
              <th className={`px-4 py-3 text-right text-xs font-medium uppercase tracking-wider ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
              }`}>
                Amount
              </th>
              <th className={`px-4 py-3 text-right text-xs font-medium uppercase tracking-wider ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
              }`}>
                Balance After
              </th>
              <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
              }`}>
                Date
              </th>
              {showActions && isAdmin && (onEditExtraService || onDeleteExtraService) && (
                <th className={`px-4 py-3 text-center text-xs font-medium uppercase tracking-wider ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                }`}>
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className={`divide-y ${
            theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'
          }`}>
            {transactions.map((transaction) => {
              const amount = parseFloat(transaction.amount);
              const isPositive = amount >= 0;
              
              return (
                <tr
                  key={transaction.id}
                  onClick={() => onTransactionClick?.(transaction)}
                  className={clsx(
                    'transition-colors',
                    onTransactionClick && 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50',
                    theme === 'dark' ? 'bg-gray-900/50' : 'bg-white'
                  )}
                >
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      {getTransactionIcon(transaction)}
                      <span className={`text-sm font-medium ${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-900'
                      }`}>
                        {getTransactionTypeLabel(transaction)}
                      </span>
                    </div>
                  </td>
                  {showHostColumn && (
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <User className="h-4 w-4 text-gray-400" />
                        <span className={`text-sm ${
                          theme === 'dark' ? 'text-gray-300' : 'text-gray-900'
                        }`}>
                          {transaction.host_name}
                        </span>
                      </div>
                    </td>
                  )}
                  <td className="px-4 py-4">
                    <div className="text-sm">
                      <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-900'}>
                        {transaction.description}
                      </p>
                      {transaction.event_title && (
                        <p className={`text-xs mt-1 ${
                          theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                        }`}>
                          Event: {transaction.event_title}
                        </p>
                      )}
                      {transaction.guest_name && (
                        <p className={`text-xs mt-1 ${
                          theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                        }`}>
                          Guest: {transaction.guest_name}
                        </p>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-right">
                    <span className={clsx(
                      'text-sm font-semibold',
                      isPositive
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400'
                    )}>
                      {formatAmount(transaction.amount)}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-right">
                    <span className={`text-sm font-medium ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-900'
                    }`}>
                      {parseFloat(transaction.balance_after).toFixed(2)} JOD
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-1 text-sm text-gray-500 dark:text-gray-400">
                      <Calendar className="h-4 w-4" />
                      <span>{formatDate(transaction.created_at)}</span>
                    </div>
                  </td>
                  {showActions && isAdmin && (onEditExtraService || onDeleteExtraService) && (
                    <td className="px-4 py-4 whitespace-nowrap text-center">
                      {transaction.transaction_type === 'extra_service' && (
                        <div className="flex items-center justify-center gap-2">
                          {onEditExtraService && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                onEditExtraService(transaction);
                              }}
                              leftIcon={<Edit className="h-3 w-3" />}
                            >
                              Edit
                            </Button>
                          )}
                          {onDeleteExtraService && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                onDeleteExtraService(transaction);
                              }}
                              leftIcon={<Trash2 className="h-3 w-3" />}
                              className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                            >
                              Delete
                            </Button>
                          )}
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TransactionTable;

